import { useLocation } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { FragmentType, Id, UserRoleId } from '@e-socna/data-layer';

import { PATH_MY_ACCOUNT_USER_AUTHORIZATIONS } from '../../../../app/constants/paths.constant';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { CheckboxList } from '../../../../components/CheckboxList';
import { LimitedFeature } from '../../../../components/LimitedFeature';

import { GlobalRolesEdition } from './GlobalRolesEdition';

export const UserPage_Authorizations_User_Fragment = graphql(`
  fragment UserPage_Authorizations_User_Fragment on User {
    _id
    globalRoleIds
    person {
      activeMemberships {
        _id
        organization {
          _id
          name
        }
        contextualRoleIds
      }
    }
  }
`);

interface Props {
  readonly user: FragmentType<typeof UserPage_Authorizations_User_Fragment>;
  // Without edit path (own account), the roles are read-only; the contextual
  // ones are edited with the memberships of the person.
  readonly paths: Readonly<{ consult: string; edit?: string }>;
}

export const Authorizations = ({ user: pUser, paths }: Readonly<Props>) => {
  const {
    _id: userId,
    globalRoleIds,
    person: { activeMemberships },
  } = getFragmentData(UserPage_Authorizations_User_Fragment, pUser);

  const { pathname: currentPath } = useLocation();

  const { userRoles } = useReferencesContext();

  const isMyAccount = currentPath === PATH_MY_ACCOUNT_USER_AUTHORIZATIONS;

  const globalRoles = userRoles.filter((userRole) => userRole.isGlobal);

  const contextualRoles = userRoles.filter((userRole) => userRole.isContextual);

  return (
    <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!isMyAccount && <LimitedFeature />}
      {!!globalRoles.length && (
        <Paper sx={{ p: 2 }}>
          <Typography gutterBottom variant="h6">
            Application e-SOCNA - Rôles globaux
          </Typography>
          <CheckboxList<Id>
            idPrefix="user-global-roles-"
            items={globalRoles.map((globalRole) => ({
              id: globalRole._id,
              label: globalRole.label,
            }))}
            value={globalRoleIds}
            variant="consultation"
          />
        </Paper>
      )}
      {activeMemberships.map((activeMembership) => (
        <Paper key={activeMembership._id} sx={{ p: 2 }}>
          <Typography gutterBottom variant="h6">
            {activeMembership.organization.name} - Rôles contextuels
          </Typography>
          <CheckboxList<Id>
            idPrefix={`user-contextual-roles-${activeMembership._id}-`}
            items={contextualRoles.map((contextualRole) => ({
              id: contextualRole._id,
              label: contextualRole.label,
            }))}
            value={activeMembership.contextualRoleIds ?? EMPTY_ARRAY}
            variant="consultation"
          />
        </Paper>
      ))}
      {!!paths.edit && (
        <GlobalRolesEdition
          globalRoleIds={globalRoleIds as readonly UserRoleId[]}
          paths={{ consult: paths.consult, edit: paths.edit }}
          userId={userId}
        />
      )}
    </Stack>
  );
};
